import { isEmpty } from 'lodash';
import { oneOf } from 'prop-types';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';

import {
  CseBenefitsList,
  CustomSlider,
  FeaturedProducts,
  FormInput,
  FormSelect,
  HeroBanner,
  InformationsSlider,
  PromoteProducts,
  SearchBar,
  useHomePageContext
} from 'components';
import { PAGE_TYPES } from 'constants';
import { useFilters } from 'hooks';
import { routes } from 'router';

const LeisureHomePage = ({ pageType }) => {
  const location = useLocation();
  const { getHomePage, homePage, homePageLoading, newsCards, featuredProducts } = useHomePageContext();

  useEffect(() => {
    // Trigger getHomePage when navigating from Leisure and Sport homepage
    getHomePage();
  }, [location.pathname]);

  return (
    <div className="homepage mb-4">
      <div className="relative">
        <HeroBanner
          image={homePage?.heroImageDesktop}
          mobileImage={homePage?.heroImageMobile}
          loading={homePageLoading}
          className="hero-container gradient-small"
        />

        <SearchBar>{pageType === PAGE_TYPES.leisures ? <KeywordsForm /> : <SportForm />}</SearchBar>
      </div>

      <div className="main-container pt-12 flex flex-col gap-24">
        <CustomSlider slides={newsCards || []} title={homePage?.newsSliderTitle || ''} loading={homePageLoading} />

        <PromoteProducts />

        {!isEmpty(homePage?.informationSlides) && <InformationsSlider slides={homePage?.informationSlides} />}

        {!isEmpty(featuredProducts) && <FeaturedProducts products={featuredProducts} />}
        <CseBenefitsList title={homePage?.cseBenefitsTitle} />
      </div>
    </div>
  );
};

const KeywordsForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm({
    mode: 'all',
    defaultValues: {
      keywords: ''
    }
  });

  const keywords = form.watch('keywords');

  const onSubmit = () => {
    const searchParams = { type: PAGE_TYPES.leisures, keywords };

    navigate({
      pathname: routes.results.path,
      search: createSearchParams(searchParams).toString()
    });
  };

  return (
    <FormProvider {...form}>
      <form className="search-bar" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grow">
          <FormInput
            name="keywords"
            placeholder={t('form:placeholders.searchLeisure')}
            label={t('form:search')}
            className="search-input"
            rules={{ required: true }}
            dataTestId="searchLeisureInput"
          />
        </div>

        <button
          type="submit"
          className="btn secondary uppercase mt-2 mx-4 lg:mt-0 lg:mr-2 xl:mr-4"
          disabled={isEmpty(keywords)}
          data-test-id="searchFormSubmit"
        >
          {t('buttons.search')}
        </button>
      </form>
    </FormProvider>
  );
};

const SportForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const form = useForm({
    mode: 'all',
    defaultValues: {
      sportCategories: []
    }
  });

  const { getSportFilters, loadingSportCategories, sportCategories } = useFilters();

  const sportCategoriesValues = form.watch('sportCategories');

  useEffect(() => {
    getSportFilters({ withCityFilter: false });
  }, []);

  const onSubmit = () => {
    const subCategory =
      form
        .getValues('sportCategories')
        ?.map((category) => category.value)
        .join('-') || '';

    const searchParams = {
      type: PAGE_TYPES.sport,
      category: form.getValues('sportCategories')?.[0]?.categoryId,
      subCategory
    };

    navigate({
      pathname: routes.results.path,
      search: createSearchParams(searchParams).toString()
    });
  };

  return (
    <FormProvider {...form}>
      <form className="search-bar" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grow p-3 lg:p-0">
          <Controller
            render={({ field: { onChange } }) => (
              <FormSelect
                onChange={onChange}
                name="sportCategories"
                options={sportCategories || []}
                className="w-full lg:w-fit"
                label={t('form:type')}
                placeholder={t('form:placeholders.selectSportCategory')}
                isLoading={loadingSportCategories}
                isMulti
              />
            )}
            name="sportCategories"
            control={form.control}
            rules={{ required: true }}
          />
        </div>

        <button
          type="submit"
          className="btn secondary uppercase mt-2 mx-4 lg:mt-0 lg:mr-2 xl:mr-4"
          disabled={isEmpty(sportCategoriesValues)}
          data-test-id="searchFormSubmit"
        >
          {t('buttons.search')}
        </button>
      </form>
    </FormProvider>
  );
};

LeisureHomePage.propTypes = {
  pageType: oneOf(['leisures', 'sport']).isRequired
};

export default LeisureHomePage;
